import { clsx, type ClassValue } from "clsx";
import { X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { forwardRef, useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { useT } from "../i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

const variantClasses: Record<Variant, string> = {
  primary: "bg-primary text-white hover:bg-primary-dark shadow-sm",
  secondary: "bg-ink text-white hover:bg-ink/90",
  ghost: "bg-transparent text-ink hover:bg-ink/5",
  outline: "bg-white border border-ink/15 text-ink hover:bg-ink/5",
  danger: "bg-red-600 text-white hover:bg-red-700",
};
const sizeClasses: Record<Size, string> = {
  sm: "min-h-[40px] px-3 text-sm",
  md: "min-h-tap px-4 text-sm",
  lg: "min-h-[52px] px-6 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean }>(
  ({ className, variant = "primary", size = "md", loading, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : null}
      {children}
    </button>
  ),
);
Button.displayName = "Button";

export function Badge({ children, tone = "neutral", className }: { children: ReactNode; tone?: "neutral" | "green" | "amber" | "red" | "blue" | "ink"; className?: string }) {
  const tones = {
    neutral: "bg-ink/8 text-ink/80",
    green: "bg-primary/15 text-primary-dark",
    amber: "bg-accent/20 text-amber-800",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    ink: "bg-ink text-white",
  };
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", tones[tone], className)}>{children}</span>;
}

export function Card({ children, className, onClick, as: Tag = "div" }: { children: ReactNode; className?: string; onClick?: () => void; as?: "div" | "section" | "article" }) {
  return (
    <Tag onClick={onClick} className={cn("rounded-2xl bg-white shadow-card border border-ink/5", onClick && "cursor-pointer hover:border-primary/40 transition-colors", className)}>
      {children}
    </Tag>
  );
}

export function CardHeader({ title, action, subtitle }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
      <div>
        <h3 className="text-base font-bold text-ink">{title}</h3>
        {subtitle ? <p className="text-sm text-ink/60 font-body normal-case tracking-normal">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: string }>(({ className, label, id, ...props }, ref) => (
  <label className="block">
    {label ? <span className="mb-1 block text-sm font-semibold text-ink/80">{label}</span> : null}
    <input
      ref={ref}
      id={id}
      className={cn("w-full min-h-tap rounded-xl border border-ink/15 bg-white px-3 text-base text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30", className)}
      {...props}
    />
  </label>
));
Input.displayName = "Input";

export function Select({ className, label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  return (
    <label className="block">
      {label ? <span className="mb-1 block text-sm font-semibold text-ink/80">{label}</span> : null}
      <select className={cn("w-full min-h-tap rounded-xl border border-ink/15 bg-white px-3 text-base text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30", className)} {...props}>
        {children}
      </select>
    </label>
  );
}

export function Textarea({ className, label, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string }) {
  return (
    <label className="block">
      {label ? <span className="mb-1 block text-sm font-semibold text-ink/80">{label}</span> : null}
      <textarea className={cn("w-full min-h-[88px] rounded-xl border border-ink/15 bg-white p-3 text-base text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30", className)} {...props} />
    </label>
  );
}

export function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex min-h-tap cursor-pointer items-center gap-2 text-sm font-semibold text-ink/80">
      <input type="checkbox" className="h-5 w-5 rounded border-ink/30 accent-[var(--brand-primary)]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: Array<{ id: T; label: string; count?: number }>; value: T; onChange: (v: T) => void }) {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto scrollbar-thin border-b border-ink/10">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            "min-h-tap whitespace-nowrap px-4 text-sm font-semibold border-b-2 -mb-px transition-colors",
            value === t.id ? "border-primary text-ink" : "border-transparent text-ink/55 hover:text-ink",
          )}
        >
          {t.label}
          {t.count != null ? <span className="ml-1.5 rounded-full bg-ink/8 px-1.5 text-xs">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Slide-over on desktop, bottom sheet on phones. */
export function Sheet({ open, onClose, title, children, side = "right" }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; side?: "right" | "bottom" }) {
  const t = useT();
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-50">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/40" onClick={onClose} />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={reduce ? { opacity: 0 } : side === "right" ? { x: "100%" } : { y: "100%" }}
            animate={reduce ? { opacity: 1 } : { x: 0, y: 0 }}
            exit={reduce ? { opacity: 0 } : side === "right" ? { x: "100%" } : { y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className={cn(
              "absolute bg-white shadow-2xl flex flex-col",
              "inset-x-0 bottom-0 max-h-[92vh] rounded-t-3xl",
              "md:inset-y-0 md:right-0 md:left-auto md:w-[440px] md:max-h-none md:rounded-none md:rounded-l-3xl",
            )}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10">
              <h2 className="text-lg font-bold">{title}</h2>
              <button aria-label={t("common.close")} onClick={onClose} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl hover:bg-ink/5">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

/** Confirmation dialog, used ONLY for irreversible actions. Buttons are labeled by outcome. */
export function ConfirmDialog({ open, title, body, confirmLabel, keepLabel, onConfirm, onKeep, danger }: { open: boolean; title: string; body: string; confirmLabel: string; keepLabel: string; onConfirm: () => void; onKeep: () => void; danger?: boolean }) {
  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/50" onClick={onKeep} />
          <motion.div role="alertdialog" aria-modal="true" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold mb-2">{title}</h2>
            <p className="text-ink/70 mb-6">{body}</p>
            <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
              <Button variant="outline" size="lg" onClick={onKeep}>{keepLabel}</Button>
              <Button variant={danger ? "danger" : "primary"} size="lg" onClick={onConfirm}>{confirmLabel}</Button>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/15 bg-white/60 px-6 py-10 text-center">
      <p className="font-semibold text-ink/80">{title}</p>
      {body ? <p className="text-sm text-ink/55 max-w-sm">{body}</p> : null}
      {action}
    </div>
  );
}

export function LoadingState({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex items-center gap-3 py-10 justify-center text-ink/60" role="status">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-ink/20 border-t-primary" />
      <span>{label ?? t("common.loading")}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800">
      <p className="font-semibold">{t("common.error")}</p>
      <p className="text-sm opacity-80 break-words">{msg}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-xl bg-ink/8", className)} />;
}
